/**
 * Toast notification system — module-level pub/sub + React component.
 *
 * Usage from anywhere (including Zustand store actions):
 *   import { showToast } from '@/components/Toast.tsx';
 *   showToast('Saved');
 *   showToast('Save failed: ...', 'error');
 */

import { useSyncExternalStore, useCallback } from 'react';
import styles from './Toast.module.css';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  text: string;
  type: ToastType;
}

// ── Module-level pub/sub ───────────────────────────────────────────────

let nextId = 0;
let toasts: Toast[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

const AUTO_DISMISS_MS = 2500;

export function showToast(text: string, type: ToastType = 'success') {
  const id = nextId++;
  toasts = [...toasts, { id, text, type }];
  emit();
  setTimeout(() => {
    toasts = toasts.filter(t => t.id !== id);
    emit();
  }, AUTO_DISMISS_MS);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function getSnapshot(): Toast[] {
  return toasts;
}

// ── React component ────────────────────────────────────────────────────

export function ToastContainer() {
  const items = useSyncExternalStore(subscribe, getSnapshot);

  const dismiss = useCallback((id: number) => {
    toasts = toasts.filter(t => t.id !== id);
    emit();
  }, []);

  if (items.length === 0) return null;

  return (
    <div className={styles.container}>
      {items.map(t => (
        <div
          key={t.id}
          className={`${styles.toast} ${t.type === 'error' ? styles.error : t.type === 'warning' ? styles.warning : t.type === 'info' ? styles.info : styles.success}`}
          onClick={() => dismiss(t.id)}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}
